import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, Typography, useTheme } from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useSelector } from "react-redux";
import { getChatMessages, getChatUsers, sendChatMessage } from "../../api/userapi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Chat = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { userloginfo } = useSelector((store) => store.userReducer);
  const [users, setUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const activePeerId = useMemo(() => (activeUser ? activeUser.userid : ""), [activeUser]);
  const isDark = theme.palette.mode === "dark";
  const panelBg = isDark ? colors.primary[400] : colors.grey[100];
  const panelBorder = isDark ? colors.grey[800] : colors.grey[300];
  const selectedUserBg = isDark ? colors.blueAccent[700] : colors.blueAccent[100];
  const selectedUserFg = isDark ? colors.grey[100] : colors.grey[900];
  const chatAreaBg = isDark ? "#f3f4f6" : panelBg;
  const chatAreaText = isDark ? "#111111" : "inherit";
  const incomingBubbleBg = isDark ? "#f3f4f6" : "#eaf0ff";
  const outgoingBubbleBg = isDark ? colors.blueAccent[600] : colors.blueAccent[400];
  const metaTextColor = isDark ? "#6b7280" : colors.grey[700];

  const showToastMessage = (type, message) => {
    toast(message, {
      type: type,
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  };

  const loadChatUsers = async () => {
    if (!userloginfo || !userloginfo.userid) return;
    const post = new FormData();
    post.append("action", "getChatUsers");
    post.append("UserID", userloginfo.userid);
    const response = await getChatUsers(post);
    if (response.status) {
      const userList = response.Message || [];
      setUsers(userList);
      setActiveUser((prevActiveUser) => {
        if (!userList.length) return null;
        if (!prevActiveUser) return userList[0];
        const matchedUser = userList.find((user) => user.userid === prevActiveUser.userid);
        return matchedUser || userList[0];
      });
    }
  };

  const loadMessages = async () => {
    if (!userloginfo || !userloginfo.userid || !activePeerId) {
      setMessages([]);
      return;
    }
    const post = new FormData();
    post.append("action", "getChatMessages");
    post.append("UserID", userloginfo.userid);
    post.append("PeerUserID", activePeerId);
    const response = await getChatMessages(post);
    if (response.status) {
      setMessages(response.Message || []);
    }
  };

  useEffect(() => {
    loadChatUsers();
  }, [userloginfo]);

  useEffect(() => {
    loadMessages();
    if (!activePeerId) return undefined;
    const syncInterval = setInterval(() => {
      loadMessages();
    }, 5000);
    return () => clearInterval(syncInterval);
  }, [activePeerId, userloginfo]);

  useEffect(() => {
    const wrap = messagesContainerRef.current;
    if (!wrap) return;
    // Never use scrollIntoView here — it scrolls ancestors/window and breaks layout.
    const scrollToBottom = () => {
      wrap.scrollTop = wrap.scrollHeight;
    };
    requestAnimationFrame(scrollToBottom);
  }, [messages, activePeerId]);

  const handleSendMessage = async () => {
    if (!activePeerId) {
      showToastMessage("error", "Please select a user to chat");
      return;
    }
    if (!messageText.trim()) return;

    setIsSending(true);
    const post = new FormData();
    post.append("action", "sendChatMessage");
    post.append("FromUserID", userloginfo.userid);
    post.append("ToUserID", activePeerId);
    post.append("Message", messageText.trim());
    const response = await sendChatMessage(post);
    if (response.status) {
      setMessageText("");
      await loadMessages();
    } else {
      showToastMessage("error", response.Message);
    }
    setIsSending(false);
  };

  return (
    <Box
      m="20px"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 92px)",
        maxHeight: "calc(100vh - 92px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <ToastContainer />
      <Header title="CHATTING" subtitle="Send text messages to users" />
      <Box
        display="grid"
        gridTemplateColumns="280px 1fr"
        gap="16px"
        mt="16px"
        sx={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          border={`1px solid ${panelBorder}`}
          borderRadius="12px"
          backgroundColor={panelBg}
          height="100%"
          minHeight="0"
          overflowY="auto"
        >
          {users.length === 0 ? (
            <Typography p="12px">No users available.</Typography>
          ) : (
            users.map((user) => (
              <Box
                key={user.userid}
                p="12px"
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isDark ? colors.primary[500] : colors.grey[200],
                  },
                }}
                color={activePeerId === user.userid ? selectedUserFg : "inherit"}
                backgroundColor={activePeerId === user.userid ? selectedUserBg : "transparent"}
                borderBottom={`1px solid ${panelBorder}`}
                onClick={() => setActiveUser(user)}
              >
                <Typography fontWeight="bold">{user.user_fullname}</Typography>
                <Typography variant="body2" color={colors.grey[300]}>
                  {user.usertype}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Box
          border={`1px solid ${panelBorder}`}
          borderRadius="12px"
          backgroundColor={chatAreaBg}
          color={chatAreaText}
          display="flex"
          flexDirection="column"
          height="100%"
          width="100%"
          minWidth="0"
          minHeight={0}
          sx={{
            overflow: "hidden",
            minHeight: 0,
            // Must shrink inside grid column or messages never overflow → no scrollbar
            maxHeight: "100%",
          }}
        >
          <Box p="12px" borderBottom={`1px solid ${panelBorder}`} flexShrink={0}>
            <Typography fontWeight="bold">
              {activeUser ? `Chat with ${activeUser.user_fullname}` : "Select a user"}
            </Typography>
          </Box>
          <Box
            ref={messagesContainerRef}
            p="12px"
            backgroundColor={chatAreaBg}
            sx={{
              // flex-basis 0 is critical — otherwise this column grows with content (no scrollbar)
              flex: "1 1 0",
              minHeight: 0,
              overflowY: "scroll",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              scrollbarWidth: "auto",
              scrollbarColor: isDark ? "#737373 #dedede" : "#64748b #e2e8f0",
              "&::-webkit-scrollbar": { width: "12px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: isDark ? "#e5e7eb" : "#e2e8f0",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "#9ca3af" : "#94a3b8",
                borderRadius: "8px",
              },
            }}
          >
            {messages.length === 0 ? (
              <Typography color={isDark ? "#6b7280" : colors.grey[300]}>No messages yet.</Typography>
            ) : (
              messages.map((msg) => {
                const mine = msg.from_user_id === userloginfo.userid;
                return (
                  <Box
                    key={msg.message_id}
                    mb="8px"
                    display="flex"
                    justifyContent={mine ? "flex-end" : "flex-start"}
                  >
                    <Box
                      px="10px"
                      py="8px"
                      borderRadius="12px"
                      maxWidth="70%"
                      backgroundColor={mine ? outgoingBubbleBg : incomingBubbleBg}
                      boxShadow={isDark ? "0 2px 8px rgba(0,0,0,0.35)" : "0 1px 4px rgba(0,0,0,0.12)"}
                    >
                      <Typography color={mine ? colors.grey[100] : "#111111"}>{msg.message_text}</Typography>
                      <Typography variant="caption" color={metaTextColor}>
                        {msg.created_at}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
            <Box ref={messagesEndRef} />
          </Box>
          <Box
            p="10px"
            borderTop={`1px solid ${panelBorder}`}
            display="flex"
            alignItems="center"
            gap="10px"
            backgroundColor={isDark ? "#eef0f4" : chatAreaBg}
            flexShrink={0}
            sx={{
              boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
              position: "relative",
              zIndex: 1,
              flexShrink: 0,
            }}
          >
            <TextField
              fullWidth
              variant="filled"
              placeholder="Type message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={!activePeerId}
              InputProps={{
                sx: {
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                  color: "#111111",
                  height: "44px",
                  border: "1px solid #cbd5e1",
                  "& input": {
                    color: "#111111",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                  "& input::placeholder": {
                    color: "#6b7280",
                    opacity: 1,
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={isSending || !activePeerId}
              sx={{
                minWidth: "78px",
                height: "44px",
                borderRadius: "10px",
                fontWeight: 700,
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;

